import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { BehaviorSubject, EMPTY } from "rxjs";
import { catchError, filter, map, switchMap, tap } from "rxjs/operators";

import { AuthModel, EXPLICIT_SELECT_ORGANISATION, PermissionDeniedError } from "@intocare/auth-data";
import { SnackbarService } from "@intocare/ui";

@Component({
	selector: "ic-sign-in",
	templateUrl: "./sign-in.component.html",
	styleUrls: ["./sign-in.component.scss"],
	standalone: false,
})
export class SignInComponent implements OnInit {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private auth = inject(AuthModel);
	private snackbarService = inject(SnackbarService);

	private loadingSubject$ = new BehaviorSubject(false);
	loading$ = this.loadingSubject$.asObservable();

	error$ = this.auth.errorMessage$.pipe(
		filter<string>(Boolean),
		tap(() => {
			this.loadingSubject$.next(false);
		}),
	);
	inactivity = this.auth.inactivity;

	organisation$ = this.route.paramMap.pipe(
		map((params: ParamMap) => {
			const org = params.get("org");
			const home = params.get("home");

			return home ? `${org}.${home}` : org;
		}),
	);
	hqName$ = this.route.paramMap.pipe(map((params: ParamMap) => params.get("org") || "intocare"));

	private static parseQueryParamState(state: string) {
		try {
			return JSON.parse(atob(decodeURIComponent(state as string)));
		} catch (err) {
			console.error(`Failed to parse query param state`, err);
			return null;
		}
	}

	private static getOrganisationFromQueryParamState(state: string) {
		const parsedState = SignInComponent.parseQueryParamState(state);
		const organisation = parsedState?.organisation;
		return typeof organisation === "string" ? organisation : "";
	}

	selectOrganisation() {
		this.router.navigate(["select-organisation"], { state: { [EXPLICIT_SELECT_ORGANISATION]: true } });
	}

	localLogin() {
		this.organisation$.subscribe({
			next: (organisation) => {
				this.router.navigate([organisation.split(".").join("/"), "sign-in-with-username"], {
					queryParamsHandling: "preserve",
				});
			},
		});
	}

	login() {
		this.loadingSubject$.next(true);
		this.organisation$
			.pipe(
				switchMap((organisation) => {
					return this.auth.getLoginUri(organisation).pipe(
						tap((uri) => {
							window.location.replace(uri);
						}),
						catchError(() => {
							this.snackbarService.show({
								error: true,
								text: "auth.errors.login_uri",
							});
							this.loadingSubject$.next(false);

							return EMPTY;
						}),
					);
				}),
			)
			.subscribe();
	}

	ngOnInit(): void {
		const { code, state } = this.route.snapshot.queryParams;

		if (code && state) {
			this.loadingSubject$.next(true);

			const organisation = SignInComponent.getOrganisationFromQueryParamState(state);

			if (organisation) {
				localStorage.setItem("currentOrganisation", organisation);
				this.auth.authenticateWithCode(organisation, code).subscribe({
					next: () => {
						this.router.navigate([...organisation.split("."), "dashboard"]);
					},
					error: (err: unknown) => {
						if (err instanceof PermissionDeniedError) {
							this.router.navigate([...organisation.split("."), "no-permissions"]);
							this.loadingSubject$.next(false);
							return;
						}
						this.loadingSubject$.next(false);
						this.snackbarService.show({
							error: true,
							text: "auth.errors.unknown",
						});
					},
					complete: () => {
						this.loadingSubject$.next(false);
					},
				});
			} else {
				this.loadingSubject$.next(false);
				this.snackbarService.show({
					error: true,
					text: "auth.errors.unknown",
				});
			}
		}
	}
}
